import { type ReactNode, useId } from "react";

export interface IndexItem {
  href: string;
  label: string;
}

interface SideIndexProps {
  /** The currently-read item, when the consumer tracks scroll position. */
  activeHref?: string;
  /** A hand-off below the rule. Hidden on one-column layouts. */
  foot?: ReactNode;
  items: IndexItem[];
  title: string;
}

/**
 * The sticky index beside a long column: FAQ topics, legal sections, the
 * headings of an article.
 *
 * Below 720px it stops being sticky and becomes a wrapping row above the
 * content it points at, so it never eats a phone viewport.
 */
export function SideIndex({
  activeHref,
  foot,
  items,
  title,
}: Readonly<SideIndexProps>) {
  // Named by the heading a reader can see, rather than by a label that repeats
  // it: the same words announced twice is the tell of a bolted-on aria fix.
  const titleId = useId();

  return (
    <nav aria-labelledby={titleId} className="side-index">
      <p id={titleId}>{title}</p>
      {items.map((item) => (
        <a
          aria-current={item.href === activeHref ? "true" : undefined}
          href={item.href}
          key={item.href}
        >
          {item.label}
        </a>
      ))}
      {foot ? <div className="index-foot">{foot}</div> : null}
    </nav>
  );
}
